import { useClient } from '@app/core/auth/profile/hooks/useClient';
import { GipReportInfo } from '@app/modules/audit/modules/global-audit/screens/gip/gip-report/config/info/GipReportInfo';
import { mapGipReportDataDtoToValues } from '@app/modules/audit/modules/global-audit/screens/gip/gip-report/config/utils/gip-report-data-dto-to-values';
import { mapValuesToGipReportDataDto } from '@app/modules/audit/modules/global-audit/screens/gip/gip-report/config/utils/gip-report-values-to-data-dto';
import { useEvidenceExportPathFormats } from '@app/modules/audit/modules/global-audit/screens/gip/gip-report/export/info/useEvidenceExportPathFormats';
import { auditReports } from '@mi-front/api-clients';
import { useStationsGroups } from '../../../../../../../../core/stations/hooks/useStationsGroupsQuery';
import { useStationsWithAudit } from '../../../../../../hooks/useStationsWithAudit';
import { AdaptiveRunActions } from '../../../aod-rule-view/custom/gip/AdaptiveRunActions';
import { AodGipExportConditionsView } from '../../../aod-rule-view/custom/gip/AodGipExportConditionsView';
import { AodGipRunDataPreview } from '../../../aod-rule-view/custom/gip/AodGipRunDataPreview';
import { AodReportFactory } from '../AodFactory';
import { AodGipExportConditionsForm } from './export-conditions/AodGipExportConditionsForm';
import { GipExportConditionsFields } from './export-conditions/GipExportConditionsFields';
import { gipExportConditionsValuesToApi } from './export-conditions/utils';
import { AodGipExportConfigInfo } from './export-info/AodGipExportConfigInfo';
import { AodGipExportOptionsForm } from './export-options/AodGipExportOptionsForm';
import { AodGipReportConfigForm } from './report-config/AodGipReportConfigForm';
import { GipAodRuleWizardCompletedValues, GipExportConditionsValues } from './types';

export type GipCreateAodRuleDto = Omit<
  auditReports.CreateAodRuleDto,
  'reportConfig' | 'exportOptions' | 'exportConditions'
> & {
  reportConfig: auditReports.GipReportDataDto;
  exportOptions: auditReports.GipEvidenceExportOptionsDto;
  exportConditions: auditReports.GipEvidenceExportConditionsDto;
};

export type AodGipFactory = AodReportFactory<
  GipAodRuleWizardCompletedValues,
  GipCreateAodRuleDto,
  auditReports.GipAodRuleDto
>;

export function useGipAodFactory(): AodGipFactory {
  const stations = useStationsWithAudit();
  const stationsGroups = useStationsGroups();
  const pathFormats = useEvidenceExportPathFormats(true);
  const client = useClient();

  return {
    fields: {
      ExportConditions: GipExportConditionsFields,
    },
    wizardForms: {
      ReportConfig: AodGipReportConfigForm,
      ExportOptions: AodGipExportOptionsForm,
      ExportConditions: AodGipExportConditionsForm,
    },
    views: {
      ReportConfig: ({ reportConfig }) => (
        <GipReportInfo
          showDateRange={false}
          config={reportConfig as unknown as auditReports.GipReportDataDto}
        />
      ),
      ExportOptions: ({ exportOptions }) => (
        <AodGipExportConfigInfo
          config={exportOptions as auditReports.GipAodRuleEvidenceExportOptionsDto}
        />
      ),
      ExportConditions: AodGipExportConditionsView,
      RunDataPreview: AodGipRunDataPreview,
      RunActions: AdaptiveRunActions,
    },
    valuesToApi: (values: GipAodRuleWizardCompletedValues): GipCreateAodRuleDto => {
      return {
        clientId: values.clientId,
        name: values.basic.name,
        trigger: values.basic.trigger,
        reportType: values.basic.reportType,
        active: values.basic.active,
        notificationSettings: values.basic.notificationSettings,
        managers:
          values.basic.managers?.map((manager) => ({
            userId: manager.id,
          })) ?? [],
        reportConfig: mapValuesToGipReportDataDto(client.id, values.reportConfig, stations),
        exportOptions: values.exportOptions,
        exportConditions: gipExportConditionsValuesToApi(values.exportConditions),
      };
    },
    apiToValues: (rule: auditReports.AodRule): GipAodRuleWizardCompletedValues | null => {
      if (!pathFormats) {
        return null;
      }

      return {
        rule,
        clientId: rule.clientId,
        basic: {
          name: rule.name,
          trigger: rule.trigger,
          reportType: rule.reportType,
          active: rule.active,
          notificationSettings: rule.notificationSettings,
          managers:
            rule.managers?.map((manager) => ({
              id: manager.userId!,
            })) || [],
        },
        reportConfig: mapGipReportDataDtoToValues(
          rule.reportConfig as auditReports.GipReportDataDto,
          stations,
          stationsGroups,
        ),
        exportOptions: rule.exportOptions,
        exportConditions: gipExportConditionsValuesFromApi(rule.exportConditions),
      };
    },
  };
}

/**
 * Converts GIP export conditions API values back to form values
 */
export function gipExportConditionsValuesFromApi(
  apiValues: auditReports.GipEvidenceExportConditionsDto,
): GipExportConditionsValues {
  const enableEfficiency = typeof apiValues.minEfficiency === 'number';
  const minEfficiency = enableEfficiency ? Math.round(apiValues.minEfficiency as number) : 0;

  return {
    enableEfficiency,
    minEfficiency,
  };
}
